import * as d3 from "d3"
import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react"
import { WebsocketContext } from "../../../../context/WebsocketContext"
import { setCanvas } from "../../../../stores/canvasSlice"
import { useAppDispatch, useAppSelector } from "../../../../stores/hooks"
import { RootState } from "../../../../stores/store"
import ConfirmDeleteCardModal from "./modals/ConfirmDeleteCardModal"
import EditCardModal from "./modals/EditCardModal"
import Postit from "./Postit"
import Activities from "./quadrants/Activities"
import ExpectedResults from "./quadrants/ExpectedResults"
import Feelings from "./quadrants/Feelings"
import ImprovementActions from "./quadrants/ImprovementActions"
import Levels from "./quadrants/Levels"
import NegativePoints from "./quadrants/NegativePoints"
import PositivePoints from "./quadrants/PositivePoints"
import Role from "./quadrants/Role"
import Toolbar from "./Toolbar"
import { t } from "i18next"
import { QuadrantType, getQuadrantLayouts } from "./QuadrantLayouts"

interface IResolution {
  width: number
  height: number
}

// Tipos e layouts movidos para QuadrantLayouts.ts

const Board = (): React.JSX.Element => {
  const { send } = useContext(WebsocketContext)
  const canvasStore = useAppSelector((state: RootState) => state.canvas)
  const dispatch = useAppDispatch()

  const [resolution, setResolution] = useState<IResolution>({
    width: window.innerWidth - 50,
    height: window.innerHeight - 150,
  })

  const svgRef = useRef<SVGSVGElement | null>(null)
  const gRef = useRef<SVGGElement | null>(null)
  const [dragging, setDragging] = useState(false)
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false)
  const [showEditCardModal, setShowEditCardModel] = useState(false)
  const [currentCard, setCurrentCard] = useState<ICard>()
  const [currentZoomTransform, setCurrentZoomTransform] = useState<d3.ZoomTransform | null>(null)
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null)

  const renderCardElements = (shape: "square" | "circle") =>
    canvasStore.cards
      .filter((card) => card.shape === shape)
      .slice()
      .sort((a, b) => (a.y ?? 0) - (b.y ?? 0))
      .map((card) => (
        <Postit
          key={card.motion_hash}
          cardData={card}
          setCurrentCardFn={setCurrentCard}
          setDraggingFn={setDragging}
          setShowEditCardModalFn={setShowEditCardModel}
          setShowConfirmDeleteModalFn={setShowConfirmDeleteModal}
        />
      ))

  const cards = () => {
    const cardElements = renderCardElements("square")
    const circleCardElements = renderCardElements("circle")

    cardElements.push(...circleCardElements)

    return cardElements
  }

  // Layouts importados de QuadrantLayouts.ts

  // Função que retorna os componentes de quadrante com base no layout
  const getQuadrantsByType = (type: QuadrantType): React.JSX.Element[] => {
    const layouts = getQuadrantLayouts(type)
    const components: React.JSX.Element[] = []

    layouts.forEach((layout) => {
      let component: React.JSX.Element | null = null

      switch (layout.id) {
        case "role":
          component = <Role key="role" x={layout.x} y={layout.y} width={layout.width} height={layout.height} />
          break
        case "activities":
          component = <Activities key="activities" x={layout.x} y={layout.y} width={layout.width} height={layout.height} />
          break
        case "expected-results":
          component = <ExpectedResults key="expected-results" x={layout.x} y={layout.y} width={layout.width} height={layout.height} />
          break
        case "levels":
          component = <Levels key="levels" evaluation_level={canvasStore.feedback?.evaluation_level} x={layout.x} y={layout.y} width={layout.width} height={layout.height} />
          break
        case "positive-points":
          component = <PositivePoints key="positive-points" x={layout.x} y={layout.y} width={layout.width} height={layout.height} />
          break
        case "negative-points":
          component = <NegativePoints key="negative-points" x={layout.x} y={layout.y} width={layout.width} height={layout.height} />
          break
        case "improvement-actions":
          component = <ImprovementActions key="improvement-actions" x={layout.x} y={layout.y} width={layout.width} height={layout.height} />
          break
        case "feelings":
          component = <Feelings key="feelings" x={layout.x} y={layout.y} width={layout.width} height={layout.height} />
          break
      }

      if (component) {
        components.push(component)
      }
    })

    return components
  }

  const quadrantType =
    canvasStore.feedback?.board_type === "version_one"
      ? QuadrantType.VERSION_ONE
      : canvasStore.feedback?.board_type === "version_two"
      ? QuadrantType.VERSION_TWO
      : QuadrantType.VERSION_THREE

  const quadrants = () => {
    return getQuadrantsByType(quadrantType)
  }

  const resizeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const handleResize = () => {
      setResolution({
        width: window.innerWidth - 50,
        height: window.innerHeight - 150,
      })
    }

    const onResize = () => {
      if (resizeTimeout.current) clearTimeout(resizeTimeout.current)
      resizeTimeout.current = setTimeout(handleResize, 250)
    }

    window.addEventListener("resize", onResize)
    return () => {
      window.removeEventListener("resize", onResize)
      if (resizeTimeout.current) clearTimeout(resizeTimeout.current)
    }
  }, [])

  const centerBoard = () => {
    if (!svgRef.current || !gRef.current || !zoomRef.current) return

    const svg = d3.select(svgRef.current)
    const gBBox = gRef.current.getBBox()
    const minScale = 0.5
    const centerX = (resolution.width - gBBox.width * minScale) / 2 - gBBox.x * minScale
    const centerY = (resolution.height - gBBox.height * minScale) / 2 - gBBox.y * minScale
    const transform = d3.zoomIdentity.translate(centerX, centerY).scale(minScale)

    svg.transition().duration(500).call(zoomRef.current.transform, transform)
    setCurrentZoomTransform(transform)
  }

  useLayoutEffect(() => {
    if (!svgRef.current || !gRef.current) return

    const svg = d3.select(svgRef.current)
    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .extent([
        [0, 0],
        [resolution.width, resolution.height],
      ])
      .translateExtent([
        [-150, -150],
        [2150, 1150],
      ])
      .scaleExtent([0.5, 3.0])
      .on("zoom", (event) => {
        if (gRef.current) {
          d3.select(gRef.current).attr("transform", event.transform.toString())
          setCurrentZoomTransform(event.transform)
        }
      })

    zoomRef.current = zoom
    svg.call(zoom).on("dblclick.zoom", null)
    centerBoard()
  }, [resolution])



  const handleZoom = (direction: "in" | "out") => {
    if (!svgRef.current || !zoomRef.current) return

    const svg = d3.select(svgRef.current)
    const svgElement = svgRef.current.getBoundingClientRect()
    const center = [svgElement.width / 2, svgElement.height / 2]
    const scaleFactor = direction === "in" ? 1.2 : 0.8

    svg.transition().duration(300).call(zoomRef.current.scaleBy, scaleFactor, center)
  }

  const handleCancelDeletePostit = () => {
    if (!currentCard) return

    setShowConfirmDeleteModal(false)
  }

  const handleConfirmDeleteCard = () => {
    if (!currentCard) return

    send("deletecard", currentCard)
    d3.select(`#group-${currentCard.id}`).remove()
    setShowConfirmDeleteModal(false)
  }

  const handleCancelEditCard = () => {
    setShowEditCardModel(false)
  }

  const handleSaveCard = (card: ICard, cardText: string) => {
    if (!card) return
    const updatedCard = { ...card, text: cardText }
    const updatedCards = canvasStore.cards.map((c) => (c.id === card.id ? updatedCard : c))

    dispatch(setCanvas({ ...canvasStore, cards: updatedCards }))
    send("editcard", updatedCard)
    setShowEditCardModel(false)
    setCurrentCard(updatedCard)
  }

  return (
    <>
      <div style={{ position: "relative", width: "100%" }}>
        {!canvasStore.feedback?.visible && (
          <div
            className="alert alert-warning d-flex align-items-center"
            role="alert"
            style={{
              position: "absolute",
              top: 5,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
              width: "max-content",
              maxWidth: "90%",
              gap: "10px",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
              width="24"
              height="24"
              fill="currentColor"
            >
              <path d="M256 0C114.836 0 0 114.836 0 256s114.836 256 256 256 256-114.836 256-256S397.164 0 256 0zm0 472c-119.103 0-216-96.897-216-216S136.897 40 256 40s216 96.897 216 216-96.897 216-216 216zm-24-336h48v160h-48zm0 192h48v48h-48z" />
            </svg>
            <div>{t('alert.cardsHidden')}</div>
          </div>
        )}
        <svg ref={svgRef} width="100%" height={resolution.height}>
          <g ref={gRef}>
            {quadrants()}
            {cards()}
          </g>
        </svg>
          <Toolbar dragging={dragging} zoomTransform={currentZoomTransform} resolution={resolution} onZoomIn={() => handleZoom("in")} onZoomOut={() => handleZoom("out")} onCenterBoard={centerBoard} />
        <ConfirmDeleteCardModal
          card={currentCard}
          show={showConfirmDeleteModal}
          onConfirmDeletePostit={() => handleConfirmDeleteCard()}
          onCancelDeletePostit={() => handleCancelDeletePostit()}
        />
        <EditCardModal
          card={currentCard}
          show={showEditCardModal}
          onCancel={() => {
            handleCancelEditCard()
          }}
          onSave={(card: ICard, cardText: string) => {
            handleSaveCard(card, cardText)
          }}
        />
      </div>
    </>
  )
}

export default Board
